/**
 * Authentication Configuration
 *
 * This file contains configuration for authentication providers.
 * Set AUTH_PROVIDER to switch between different authentication methods.
 */

import { ENV } from './env';

export type AuthProviderType = 'supabase' | 'google_identity_platform';

/** Where the browser sends API requests in production Firebase builds. */
export type ApiBackend = 'cloud_run' | 'external';

const normalizeOrigin = (url: string): string => url.replace(/\/$/, '');

/**
 * cloud_run (default): production builds use same-origin `/api/**`; Firebase Hosting rewrites to Cloud Run.
 * external: production builds call VITE_API_BASE_URL directly (AWS ALB, custom domain, etc.).
 * Requires HTTPS on the API when the app is served from Firebase Hosting.
 */
export const resolveApiBackend = (): ApiBackend => {
  const raw = (ENV.VITE_API_BACKEND || 'cloud_run').trim().toLowerCase();
  return raw === 'external' ? 'external' : 'cloud_run';
};

/**
 * Resolve the axios/fetch API base URL for the current runtime.
 */
export const resolveApiBaseUrl = (): string => {
  // Vite dev server (HMR): always use explicit localhost / env URL.
  if (import.meta.hot != null) {
    return normalizeOrigin(ENV.VITE_API_BASE_URL || 'http://localhost:8000');
  }

  if (resolveApiBackend() === 'external') {
    const url = normalizeOrigin(ENV.VITE_API_BASE_URL || '');
    if (!url) {
      throw new Error(
        'VITE_API_BASE_URL is required when VITE_API_BACKEND=external. Set it in .env before building.',
      );
    }
    return url;
  }

  // cloud_run: same-origin relative paths; Firebase Hosting proxies /api/** to Cloud Run.
  return '';
};

/**
 * Optional direct Cloud Run origin for long-lived chat streams in cloud_run mode only.
 * When unset, chat uses the same base as other API calls (relative /api in production).
 */
export const resolveChatStreamApiBase = (): string => {
  if (import.meta.hot != null) {
    return resolveApiBaseUrl();
  }

  if (resolveApiBackend() === 'external') {
    return resolveApiBaseUrl();
  }

  // cloud_run: keep streams same-origin via Firebase rewrite (Safari cookie compatibility)
  return resolveApiBaseUrl();
};

// Configuration constants
export const AUTH_CONFIG = {
  // Toggle between 'supabase' and 'google_identity_platform'
  AUTH_PROVIDER: (ENV.VITE_AUTH_PROVIDER || 'google_identity_platform') as AuthProviderType,

  API_BACKEND: resolveApiBackend(),
  API_BASE_URL: resolveApiBaseUrl(),

  // Google Identity Platform configuration
  GOOGLE_IDENTITY_PLATFORM: {
    PROJECT_ID: ENV.VITE_FIREBASE_PROJECT_ID || '',
    API_KEY: ENV.VITE_FIREBASE_API_KEY || '',
    AUTH_DOMAIN: ENV.VITE_FIREBASE_AUTH_DOMAIN || '',
  },

  // Supabase configuration
  SUPABASE: {
    URL: ENV.VITE_SUPABASE_URL || '',
    ANON_KEY: ENV.VITE_SUPABASE_ANON_KEY || '',
  },

  // Session configuration
  SESSION_EXPIRY_DAYS: 10,
  SESSION_REFRESH_THRESHOLD: 5 * 60 * 1000, // 5 minutes

  /**
   * Auth-only HTTP timeout (login, refresh, /me, etc.).
   * The shared apiClient defaults to 120s for long-running operations (e.g. documents).
   */
  AUTH_HTTP_TIMEOUT_MS: 30_000,
} as const;

// Helper function to get current auth provider
export const getCurrentAuthProvider = (): AuthProviderType => {
  return AUTH_CONFIG.AUTH_PROVIDER;
};

// Helper function to check if Google Identity Platform is enabled
export const isGoogleIdentityPlatformEnabled = (): boolean => {
  return AUTH_CONFIG.AUTH_PROVIDER === 'google_identity_platform';
};

// Helper function to check if Supabase is enabled
export const isSupabaseEnabled = (): boolean => {
  return AUTH_CONFIG.AUTH_PROVIDER === 'supabase';
};

// Environment validation
export const validateAuthConfig = (): void => {
  if (AUTH_CONFIG.AUTH_PROVIDER === 'google_identity_platform') {
    const missing = [];
    if (!AUTH_CONFIG.GOOGLE_IDENTITY_PLATFORM.PROJECT_ID) {
      missing.push('VITE_FIREBASE_PROJECT_ID');
    }
    if (!AUTH_CONFIG.GOOGLE_IDENTITY_PLATFORM.API_KEY) {
      missing.push('VITE_FIREBASE_API_KEY');
    }
    if (!AUTH_CONFIG.GOOGLE_IDENTITY_PLATFORM.AUTH_DOMAIN) {
      missing.push('VITE_FIREBASE_AUTH_DOMAIN');
    }

    if (missing.length > 0) {
      throw new Error(
        `Google Identity Platform configuration missing: ${missing.join(', ')}. Please check your .env file.`,
      );
    }
  } else if (AUTH_CONFIG.AUTH_PROVIDER === 'supabase') {
    if (!AUTH_CONFIG.SUPABASE.URL) {
      console.warn('VITE_SUPABASE_URL is not set for Supabase');
    }
    if (!AUTH_CONFIG.SUPABASE.ANON_KEY) {
      console.warn('VITE_SUPABASE_ANON_KEY is not set for Supabase');
    }
  }
};
